import { Effect, all, call, delay, put, race, take, takeLatest } from 'redux-saga/effects';
import * as actionTypes from '../actionTypes';
import store from 'redux/store/configStore';
import { createSpatialViewFailure, createSpatialViewSuccess, deleteSpatialViewFailure, deleteSpatialViewSuccess, fetchGlbUrl, fetchGlbUrlFailure, fetchGlbUrlSuccess, fetchLivePollingFailure, fetchLivePollingSuccess, fetchSensorDevicesFailure, fetchSensorDevicesSuccess, fetchSpatialViewOperationsFailure, fetchSpatialViewOperationsSuccess, fetchTatamDevicesFailure, fetchTatamDevicesSuccess, fetchTotalDevicesFailure, fetchTotalDevicesSuccess, getSpatialView, getSpatialViewFailure, getSpatialViewSuccess, installSpatialViewDeviceFailure, installSpatialViewDeviceSuccess, uninstallSpatialViewDeviceFailure, uninstallSpatialViewDeviceSuccess, updateModelSuccess, uploadFileToS3, uploadFileToS3Failure, uploadFileToS3Success } from 'redux/actions/spatialViewActions';
import { CustomerList, DevicesDetails, SensorDevice, SpatialViewOperation } from 'redux/types/spatialViewActionModel';
import { createSpatialViewCustomersAPI, deleteSpatialViewAPI, fetchGlbUrlAPI, fetchLivePollingAPI, fetchSensorDevicesAPI, fetchSpatialViewOperationsAPI, fetchTotalDevicesAPI, getSpatialViewCustomersAPI, installSpatialViewDeviceAPI, uninstallSpatialViewDeviceAPI, updateModelAPI, updateSpatialViewCustomersAPI, uploadFileToPresignedUrl } from 'api/spatialViewAPI';
import { setToastAction } from 'redux/actions';
import { ToastVariant } from 'shared/utils/Constants';
import { t } from 'i18next';
import { AllocatedThingsList } from 'shared/model/SiteModel';

function* getSpatialViewCustomers(action: Effect) {
    try {
        const response: CustomerList = yield call(getSpatialViewCustomersAPI,action.payload?.id);
        // Normalize response into array
        const dataArray = Array.isArray(response)
            ? response
            : [response];

        const requestedOrgId = action.payload?.id;
        const formattedResponse: CustomerList = dataArray.map(
            (item: any) => ({
                org_id: item.org_id ?? requestedOrgId,
                customer_name: item.customer_name ?? 'My Buildings',
                no_of_buildings: item.buildings?.length ?? 0,
                buildings: item.buildings ?? [],
            })
        );
        yield put(getSpatialViewSuccess(formattedResponse));
    } catch (e: any) {
        yield put(
            getSpatialViewFailure({
                error: e?.response?.data,
            }),
        );
    }
}

function* createSpatialViewSaga(action: Effect) {
    try {

        const { file, ...apiBody } = action.payload;
        const response: { file_upload_url: string, backup_file_upload_url: string } = yield call(createSpatialViewCustomersAPI, apiBody)
        yield put(createSpatialViewSuccess(response));
        yield put(uploadFileToS3());
        yield call(uploadFileToPresignedUrl, response.file_upload_url, file[0]);
        yield call(uploadFileToPresignedUrl, response.backup_file_upload_url, file[0]);
        yield put(uploadFileToS3Success())
        yield put(setToastAction(ToastVariant.SUCCESS, t('file_uploaded_successfully')));
    } catch (e: any) {
        yield put(
            createSpatialViewFailure({
                error: e?.response?.data,
            }),
        );
        yield put(uploadFileToS3Failure({ error: e?.response?.data }));
    }
}


function* updateSpatialViewSaga(action: Effect) {
    try {
        const { file, ...apiBody } = action.payload;

        const response: { file_upload_url: string, backup_file_upload_url: string } = yield call(updateSpatialViewCustomersAPI, apiBody);
        yield put(createSpatialViewSuccess(response));

        // Only upload to S3 if user selected a new file
        if (file && file.length > 0) {
            yield put(uploadFileToS3());
            yield call(uploadFileToPresignedUrl, response.file_upload_url, file[0]);
            yield call(uploadFileToPresignedUrl, response.backup_file_upload_url, file[0]);
            yield put(uploadFileToS3Success());
            yield put(setToastAction(ToastVariant.SUCCESS, t('file_uploaded_successfully')));
        } else {
            yield put(uploadFileToS3Success());
            yield put(setToastAction(ToastVariant.SUCCESS, t('spatial_view_updated_successfully')));
        }
    } catch (e: any) {
        yield put(
            createSpatialViewFailure({
                error: e?.response?.data,
            }),
        );
        yield put(uploadFileToS3Failure({ error: e?.response?.data }));
    }
}

function* fetchGlbUrlSaga(action: Effect) {
    try {
        const response: string = yield call(fetchGlbUrlAPI, action.payload);
        yield put(fetchGlbUrlSuccess(response));
    } catch (e: any) {
        yield put(fetchGlbUrlFailure({ error: e?.response?.data }));
    }
}

// spatialViewSaga.ts
function* deleteSpatialViewSaga(action: Effect) {
    try {
        const { spatial_view_id } = action.payload;

        yield call(deleteSpatialViewAPI, spatial_view_id);
        yield put(deleteSpatialViewSuccess({}));
        yield put(getSpatialView()); // refresh the list
        yield put(setToastAction(ToastVariant.SUCCESS, t('spatial_view_deletion')));
    } catch (e: any) {
        yield put(
            deleteSpatialViewFailure({
                error: e?.response?.data,
            }),
        );
    }
}


function* updateModelSaga(action: Effect) {
    try {

        const { file, spatial_view_id } = action.payload;
        const response: string = yield call(updateModelAPI, spatial_view_id)
        yield put(updateModelSuccess(response));
        yield put(uploadFileToS3());
        yield call(uploadFileToPresignedUrl, response, file[0]);
        yield put(uploadFileToS3Success())
        yield put(setToastAction(ToastVariant.SUCCESS, t('file_uploaded_successfully')));
        yield put(fetchGlbUrl({ spatial_model_id: spatial_view_id }))
    } catch (e: any) {
        yield put(
            createSpatialViewFailure({
                error: e?.response?.data,
            }),
        );
        yield put(uploadFileToS3Failure({ error: e?.response?.data }));
    }
}

function* fetchSensorDevicesSaga(action: Effect) {
    try {
        const response: AllocatedThingsList[] = yield call(fetchSensorDevicesAPI, action.payload);
        yield put(fetchSensorDevicesSuccess(response));
    } catch (e: any) {
        yield put(fetchSensorDevicesFailure({ error: e?.response?.data }));
    }
}
function* fetchTatamDevicesSaga(action: Effect) {
    try {
        const response: AllocatedThingsList[] = yield call(fetchSensorDevicesAPI, action.payload);
        yield put(fetchTatamDevicesSuccess(response));
    } catch (e: any) {
        yield put(fetchTatamDevicesFailure({ error: e?.response?.data }));
    }
}

function* installSpatialViewDeviceSaga(action: Effect) {
    try {
        const response: string = yield call(installSpatialViewDeviceAPI, action.payload);
        yield put(installSpatialViewDeviceSuccess(response));
        yield put(setToastAction(ToastVariant.SUCCESS, t('device_installed_success')));
    } catch (e: any) {
        yield put(installSpatialViewDeviceFailure({ error: e?.response?.data }));
    }
}

function* uninstallSpatialViewDeviceSaga(action: Effect) {
    try {
        const response: string = yield call(uninstallSpatialViewDeviceAPI, action.payload);
        yield put(uninstallSpatialViewDeviceSuccess(response));
        yield put(setToastAction(ToastVariant.SUCCESS, t('device_uninstalled_success')));
    } catch (e: any) {
        yield put(uninstallSpatialViewDeviceFailure({ error: e?.response?.data }));
    }
}



function* fetchTotalDevicesSaga(action: Effect) {
    try {
        const response: DevicesDetails = yield call(fetchTotalDevicesAPI, action.payload);
        yield put(fetchTotalDevicesSuccess(response));
    } catch (e: any) {
        yield put(fetchTotalDevicesFailure({ error: e?.response?.data }));
    }
}

function* livePollingWorker(action: Effect) {
    while (true) {
        try {
            const response: string = yield call(fetchLivePollingAPI, action.payload);
            yield put(fetchLivePollingSuccess(response));
        } catch (e: any) {
            yield put(fetchLivePollingFailure({ error: e?.response?.data }));
        }

        // Wait 10 seconds before next poll OR stop if STOP_LIVE_POLLING is dispatched
        const { stopped } = yield race({
            delayed: delay(60000), // poll every 10 seconds
            stopped: take(actionTypes.STOP_LIVE_POLLING),
        });

        if (stopped) break; // exit loop when stop action is dispatched
    }
}

function* fetchLivePostSaga(action: Effect) {
    yield race({
        task: call(livePollingWorker, action),
        cancel: take(actionTypes.STOP_LIVE_POLLING),
    });
}

function* fetchSpatialViewOperationsSaga(action: Effect) {
    try {
        const response: SpatialViewOperation[] = yield call(fetchSpatialViewOperationsAPI, action.payload);
        const dataArray = Array.isArray(response) ? response : [response];
        yield put(fetchSpatialViewOperationsSuccess(dataArray));
    } catch (e: any) {
        yield put(fetchSpatialViewOperationsFailure({ error: e?.response?.data }));
    }
}

function* spatialViewSaga() {
    yield all([takeLatest(actionTypes.GET_SPATIAL_VIEW, getSpatialViewCustomers)]);
    yield all([takeLatest(actionTypes.CREATE_SPATIAL_VIEW, createSpatialViewSaga)]);
    yield all([takeLatest(actionTypes.UPDATE_SPATIAL_VIEW, updateSpatialViewSaga)]);
    yield all([takeLatest(actionTypes.DELETE_SPATIAL_VIEW, deleteSpatialViewSaga)]);
    yield all([takeLatest(actionTypes.FETCH_GLB_URL, fetchGlbUrlSaga)]);
    yield all([takeLatest(actionTypes.UPDATE_MODEL, updateModelSaga)]);
    yield all([takeLatest(actionTypes.FETCH_SENSOR_DEVICES, fetchSensorDevicesSaga)]);
    yield all([takeLatest(actionTypes.FETCH_TATAM_DEVICES, fetchTatamDevicesSaga)]);
    yield all([takeLatest(actionTypes.INSTALL_SPATIAL_VIEW_DEVICE, installSpatialViewDeviceSaga)]);
    yield all([takeLatest(actionTypes.UNINSTALL_SPATIAL_VIEW_DEVICE, uninstallSpatialViewDeviceSaga)]);
    yield all([takeLatest(actionTypes.FETCH_TOTAL_DEVICES, fetchTotalDevicesSaga)]);
    yield all([takeLatest(actionTypes.FETCH_LIVE_POLLING, fetchLivePostSaga)]);
    yield all([takeLatest(actionTypes.FETCH_SPATIAL_VIEW_OPERATIONS, fetchSpatialViewOperationsSaga)]);

}

export default spatialViewSaga;
